// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EmptyState } from '@/components/empty-state';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import { ArrowOutwardRounded } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { useTableSort } from '@/hooks/use-table-sort';
import { ItemSortField } from '@/items/api/types';
import { ItemsTableRow } from '@/items/components/items-table-row';
import { ITEM_TYPE_LABELS, ItemTypeFilter } from '@/items/constants/item-types';
import { useItems } from '@/items/hooks/use-items';
import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';

const PAGE_SIZE = 50;

interface Props {
  /**
   * The item type filter to apply.
   */
  itemType: ItemTypeFilter;
  /**
   * Search query to filter by name or SKU.
   */
  searchQuery?: string;
  /**
   * The current sort state of the table.
   */
  sort: Sort<ItemSortField>;
  /**
   * Callback when a sort header is clicked.
   */
  onSort: (property: ItemSortField) => void;
  /**
   * Current page number (1-indexed).
   */
  page?: number;
  /**
   * Callback when page changes.
   */
  onPageChange: (page: number) => void;
  /**
   * Whether to include archived items.
   */
  includeArchived?: boolean;
}

/**
 * Table component for displaying unified items.
 * Shows items of all types (Service, Device, Bulk) with sorting and filtering.
 */
export function ItemsTable(props: Props) {
  const {
    itemType,
    searchQuery,
    sort,
    onSort,
    page = 1,
    onPageChange,
    includeArchived,
  } = props;

  const { items, total } = useItems({
    type: itemType,
    search: searchQuery,
    sort: sort.property,
    direction: sort.direction,
    page,
    limit: PAGE_SIZE,
    archived: includeArchived,
  });

  // Handle page overrun (e.g., if filters reduce total items)
  useEffect(() => {
    if (items.length === 0 && page > 1) {
      onPageChange(1);
    }
  }, [items.length, page, onPageChange]);

  const { getSortDirection, getLabelDirection, isLabelActive } =
    useTableSort(sort);

  // Check if any items have integrations
  const hasIntegration = items.some((item) => item.integrations.length > 0);

  const createSortHandler = (column: ItemSortField) => () => {
    onSort(column);
  };

  const getEmptyStateMessage = () => {
    if (searchQuery) {
      return 'No items match your search';
    }
    if (itemType !== 'all') {
      return `No ${ITEM_TYPE_LABELS[itemType].toLowerCase()} items found`;
    }
    return 'No items found';
  };

  if (items.length === 0) {
    return (
      <EmptyState title={getEmptyStateMessage()}>
        {!searchQuery && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography color="text.secondary">
              To start tracking items in Hardfin, create a new item
            </Typography>
            <ArrowOutwardRounded fontSize="small" color="action" />
          </Stack>
        )}
      </EmptyState>
    );
  }

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="medium" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {/* Integration status column - only shown if any items have integrations */}
              {hasIntegration && (
                <TableCell
                  sx={{
                    width: '3rem',
                    px: 2,
                    whiteSpace: 'nowrap',
                  }}
                />
              )}
              {/* Type column */}
              <TableCell
                sortDirection={getSortDirection('type')}
                sx={{ width: 180 }}
              >
                <TableSortLabel
                  active={isLabelActive('type')}
                  direction={getLabelDirection('type')}
                  onClick={createSortHandler('type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              {/* Name column */}
              <TableCell
                sortDirection={getSortDirection('name')}
                sx={{ minWidth: 150 }}
              >
                <TableSortLabel
                  active={isLabelActive('name')}
                  direction={getLabelDirection('name')}
                  onClick={createSortHandler('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              {/* SKU column */}
              <TableCell
                sortDirection={getSortDirection('sku')}
                sx={{ width: 225 }}
              >
                <TableSortLabel
                  active={isLabelActive('sku')}
                  direction={getLabelDirection('sku')}
                  onClick={createSortHandler('sku')}
                >
                  SKU
                </TableSortLabel>
              </TableCell>
              {/* Description column */}
              <TableCell sx={{ minWidth: 200 }}>Description</TableCell>
              {/* Asset count column - only shown for DEVICE type, em dash for others */}
              <TableCell align="right" sx={{ width: 120 }}>
                Assets
              </TableCell>
              {/* Spacer */}
              <TableCell sx={{ width: 24 }} />
              {/* Last updated column */}
              <TableCell
                sortDirection={getSortDirection('lastUpdatedAt')}
                sx={{ width: 180 }}
              >
                <TableSortLabel
                  active={isLabelActive('lastUpdatedAt')}
                  direction={getLabelDirection('lastUpdatedAt')}
                  onClick={createSortHandler('lastUpdatedAt')}
                >
                  Last updated
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <ItemsTableRow
                key={item.id}
                item={item}
                hasIntegration={hasIntegration}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePaginationFooter
        pagination={{
          page,
          pageSize: PAGE_SIZE,
          onPageChange,
        }}
        totalCount={total}
        entityOptions={{ singular: 'item', includeCount: false }}
        downloadURL={new URL('/v1/export/items', import.meta.env.VITE_API_URL)}
        downloadLabel="Export items"
      />
    </>
  );
}
